/**
 * Game Bridge Service
 * 
 * Central service for SDK-compatible communication between webplay host
 * and game iframes. Handles bidirectional postMessage communication.
 * 
 * Key responsibilities:
 * - Format events to match Beemi SDK expected structure
 * - Forward stream events to game
 * - Handle incoming messages from game (SDK ready, app-bridge requests)
 * - Manage iframe lifecycle (attach/detach)
 */

import {
  StreamingComment,
  StreamingGift,
  StreamingLike,
  StreamingMetrics,
  StreamingRoomInfo,
  StreamingPlatform,
  ConnectionState
} from '../../types/streaming';

// ============================================================================
// Types
// ============================================================================

/** User data structure matching SDK expectations */
export interface SDKUserData {
  id: string;
  username: string;
  display_name: string;
  image_url: string | null;
}

/** App preferences structure */
export interface AppPreferences {
  language: string;
  region: string;
  theme: 'light' | 'dark';
  locale: string;
}

/** SDK ready message from game */
export interface SDKReadyMessage {
  type: 'beemi-sdk';
  action: 'ready' | 'error';
  version: string;
  modules: string[];
  timestamp: number;
  error?: string;
}

/** App-bridge request from game */
export interface AppBridgeRequest {
  type: 'beemi-app-bridge';
  action: string;
  payload: any;
  requestId: number;
  timestamp: number;
  fireAndForget: boolean;
}

/** Generic message from game */
export type GameMessage = SDKReadyMessage | AppBridgeRequest | { type: string; [key: string]: any };

/** Callback for handling messages from game */
export type GameMessageHandler = (message: GameMessage) => void;

/** Callback for SDK ready event */
export type SDKReadyHandler = (info: { version: string; modules: string[] }) => void;

// ============================================================================
// SDK Event Formatters
// DEPRECATED: These formatters are replaced by @beemi/stream-parser library
// They're kept here temporarily for backward compatibility
// TODO: Remove once @beemi/stream-parser is published and integrated
// ============================================================================

/**
 * Format a streaming comment to SDK stream-chat event
 * @deprecated Use @beemi/stream-parser instead
 */
function formatStreamChat(comment: StreamingComment): object {
  return {
    type: 'stream-chat',
    data: {
      user: {
        id: `${comment.platform}_${comment.username}`,
        username: comment.username,
        displayName: comment.displayName || comment.username,
        platform: comment.platform,
        imageUrl: comment.imageUrl || null
      },
      message: comment.comment,
      messageId: comment.id,
      platform: comment.platform,
      timestamp: comment.timestamp
    }
  };
}

/**
 * Format a streaming gift to SDK stream-gift event
 * @deprecated Use @beemi/stream-parser instead
 */
function formatStreamGift(gift: StreamingGift): object {
  return {
    type: 'stream-gift',
    data: {
      user: {
        id: `${gift.platform}_${gift.username}`,
        username: gift.username,
        displayName: gift.displayName || gift.username,
        platform: gift.platform,
        imageUrl: gift.imageUrl || null
      },
      giftName: gift.giftName,
      giftCount: gift.giftCount,
      value: gift.value || 0,
      totalValue: (gift.value || 0) * gift.giftCount,
      giftId: gift.id,
      messageId: gift.id,
      platform: gift.platform,
      timestamp: gift.timestamp
    }
  };
}

/**
 * Format a streaming like to SDK stream-like event
 * @deprecated Use @beemi/stream-parser instead
 */
function formatStreamLike(like: StreamingLike): object {
  return {
    type: 'stream-like',
    data: {
      user: {
        id: `${like.platform}_${like.username}`,
        username: like.username,
        displayName: like.displayName || like.username,
        platform: like.platform,
        imageUrl: null
      },
      count: like.likeCount,
      platform: like.platform,
      timestamp: like.timestamp
    }
  };
}

/**
 * Format user data for SDK
 */
function formatUserData(user: SDKUserData): object {
  return {
    type: 'user-data-received',
    data: user
  };
}

/**
 * Format user signed out event
 */
function formatUserSignedOut(): object {
  return {
    type: 'user-signed-out',
    data: null
  };
}

/**
 * Format app preferences for SDK
 */
function formatAppPreferences(preferences: AppPreferences): object {
  return {
    type: 'beemi-app-preferences',
    data: preferences
  };
}

/**
 * Format app-bridge response
 */
function formatAppBridgeResponse(requestId: number, success: boolean, data?: any, error?: string): object {
  return {
    type: 'beemi-app-bridge-response',
    requestId,
    success,
    data,
    error
  };
}

// ============================================================================
// Game Bridge Service Class
// ============================================================================

class GameBridgeService {
  private iframe: HTMLIFrameElement | null = null;
  private messageHandler: ((event: MessageEvent) => void) | null = null;
  private gameMessageHandlers: Set<GameMessageHandler> = new Set();
  private sdkReadyHandlers: Set<SDKReadyHandler> = new Set();
  private isSDKReady = false;
  private sdkVersion: string | null = null;
  private sdkModules: string[] = [];
  private pendingEvents: object[] = [];
  private targetOrigin = '*'; // Will be set based on iframe src

  /**
   * Attach to an iframe and start listening for messages
   */
  attach(iframe: HTMLIFrameElement): void {
    if (this.iframe) {
      this.detach();
    }

    this.iframe = iframe;
    this.isSDKReady = false;
    this.pendingEvents = [];

    // Try to extract origin from iframe src
    try {
      const iframeSrc = iframe.src;
      if (iframeSrc) {
        const url = new URL(iframeSrc);
        this.targetOrigin = url.origin;
        console.log('🎯 [GameBridge] Target origin set to:', this.targetOrigin, '| Iframe src:', iframeSrc);
      }
    } catch (error) {
      console.warn('⚠️ [GameBridge] Failed to parse iframe origin, using wildcard:', error);
      this.targetOrigin = '*';
    }

    // Set up message listener
    this.messageHandler = this.handleMessage.bind(this);
    window.addEventListener('message', this.messageHandler);
    
    // Inject SDK after iframe loads (like React Native does)
    this.injectSDKWhenReady(iframe);
  }

  /**
   * Inject SDK into iframe after it loads (similar to React Native's injectJavaScript)
   */
  private injectSDKWhenReady(iframe: HTMLIFrameElement): void {
    const injectSDK = () => {
      try {
        // Check if iframe is from preview server (different origin = SDK already injected server-side)
        const iframeSrc = iframe.src || '';
        const iframeUrl = new URL(iframeSrc, window.location.href);
        const isSameOrigin = iframeUrl.origin === window.location.origin;
        
        if (!isSameOrigin) {
          console.log('[GameBridge] Cross-origin iframe detected (preview server), SDK already injected server-side, skipping client injection');
          
          // For cross-origin iframes, we cannot access window properties
          // The SDK ready state will be detected via postMessage from the game
          console.log('[GameBridge] Waiting for SDK ready message via postMessage...');
          
          // Request SDK status in case we missed the initial ready message (race condition)
          setTimeout(() => {
            if (!this.isSDKReady && iframe.contentWindow) {
              console.log('[GameBridge] Requesting SDK status from iframe...');
              iframe.contentWindow.postMessage({
                type: 'beemi-request',
                action: 'get-sdk-status'
              }, '*');
            }
          }, 200);
          
          return;
        }
        
        if (!iframe.contentWindow) {
          console.error('[GameBridge] Cannot inject SDK: iframe contentWindow not available');
          return;
        }
        
        // Create script element in iframe
        const doc = iframe.contentWindow.document;
        const script = doc.createElement('script');
        // Use relative path since game is proxied through same origin
        script.src = '/sdk/beemi-sdk-2.1.4.js';
        script.async = false; // Load synchronously
        
        script.onload = () => {
          // Initialize the SDK (like React Native does)
          try {
            const win = iframe.contentWindow as any;
            if (win.initBeemiModularSDK) {
              // Initialize with all modules (matching manifest)
              win.initBeemiModularSDK({
                sdkVersion: '2.1.4',
                core: { required: true },
                streams: { required: true },
                user: { required: true },
                'app-bridge': { required: true }
              });
              
              // Call game's ready callback (like React Native does)
              setTimeout(() => {
                if (typeof win.onSDKReady === 'function') {
                  win.onSDKReady();
                } else if (typeof win.gameReady === 'function') {
                  win.gameReady();
                }
              }, 100);
            } else {
              console.error('[GameBridge] initBeemiModularSDK not found');
            }
          } catch (error) {
            console.error('[GameBridge] SDK initialization error:', error);
          }
        };
        
        script.onerror = (error) => {
          console.error('[GameBridge] SDK script failed to load:', error);
        };
        
        // Append to iframe's head
        doc.head.appendChild(script);
        
      } catch (error) {
        // Cross-origin error - cannot inject
        console.error('[GameBridge] Cannot inject SDK (cross-origin):', error);
      }
    };

    // Since attach() is called from handleIframeLoad, the iframe has already loaded
    // Inject SDK immediately (small delay to ensure DOM is ready)
    setTimeout(injectSDK, 100);
  }

  /**
   * Detach from iframe and cleanup
   */
  detach(): void {
    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler);
      this.messageHandler = null;
    }

    this.iframe = null;
    this.isSDKReady = false;
    this.sdkVersion = null;
    this.sdkModules = [];
    this.pendingEvents = [];

    console.log('[GameBridge] Detached from iframe');
  }

  /**
   * Handle incoming messages from iframe
   */
  private handleMessage(event: MessageEvent): void {
    // Log all messages for debugging
    if (event.data?.type === 'beemi-sdk') {
      console.log('[GameBridge] Message received, checking source...', {
        hasIframe: !!this.iframe,
        sourceMatches: this.iframe ? event.source === this.iframe.contentWindow : 'no iframe',
        eventOrigin: event.origin,
        messageType: event.data.type
      });
    }
    
    // Verify message is from our iframe
    if (this.iframe && event.source !== this.iframe.contentWindow) {
      // Ignore messages from other sources
      if (event.data?.type === 'beemi-sdk') {
        console.warn('[GameBridge] Ignoring beemi-sdk message from non-iframe source');
      }
      return;
    }
    
    console.log('[GameBridge] Received message from iframe:', event.data?.type || event.data);

    let message: GameMessage;
    
    try {
      // Handle both string and object messages
      message = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
    } catch {
      return; // Ignore non-JSON messages
    }

    if (!message || typeof message.type !== 'string') {
      return;
    }

    // Handle SDK ready message
    if (message.type === 'beemi-sdk') {
      const sdkMessage = message as SDKReadyMessage;
      
      console.log('[GameBridge] Received beemi-sdk message:', {
        action: sdkMessage.action,
        version: sdkMessage.version,
        modules: sdkMessage.modules,
        timestamp: sdkMessage.timestamp
      });
      
      if (sdkMessage.action === 'ready') {
        console.log('[GameBridge] Processing SDK ready message...');
        this.isSDKReady = true;
        this.sdkVersion = sdkMessage.version;
        this.sdkModules = sdkMessage.modules || [];
        
        console.log(`[GameBridge] SDK marked as ready (v${this.sdkVersion}), notifying ${this.sdkReadyHandlers.size} handlers`);
        
        // Notify SDK ready handlers
        this.sdkReadyHandlers.forEach(handler => {
          try {
            handler({ version: this.sdkVersion!, modules: this.sdkModules });
          } catch (error) {
            console.error('[GameBridge] SDK ready handler error:', error);
          }
        });
        
        console.log('[GameBridge] ✅ SDK ready notification complete');
        
        // Flush pending events
        this.flushPendingEvents();
      } else if (sdkMessage.action === 'error') {
        console.error('[GameBridge] SDK error:', sdkMessage.error);
      }
      
      return;
    }

    // Handle app-bridge requests
    if (message.type === 'beemi-app-bridge') {
      // Route to handlers
      this.notifyMessageHandlers(message);
      return;
    }

    // Forward other messages to handlers
    this.notifyMessageHandlers(message);
  }

  /**
   * Notify all message handlers
   */
  private notifyMessageHandlers(message: GameMessage): void {
    this.gameMessageHandlers.forEach(handler => {
      try {
        handler(message);
      } catch (error) {
        console.error('[GameBridge] Message handler error:', error);
      }
    });
  }

  /**
   * Send a message to the iframe
   */
  private postMessage(message: object): void {
    if (!this.iframe?.contentWindow) {
      console.warn('⚠️ [GameBridge] Cannot postMessage: iframe or contentWindow not available');
      return;
    }

    try {
      // SDK 2.1.4+ accepts both string and object, send as string (more compatible)
      const messageStr = JSON.stringify(message);
      console.log('📨 [GameBridge] postMessage to iframe:', (message as any).type || 'unknown', '→', this.targetOrigin);
      console.log('📨 [GameBridge] Message content:', messageStr.substring(0, 200));
      console.log('📨 [GameBridge] contentWindow exists:', !!this.iframe.contentWindow);
      this.iframe.contentWindow.postMessage(messageStr, this.targetOrigin);
      console.log('✅ [GameBridge] postMessage call completed (no error thrown)');
    } catch (error) {
      console.error('❌ [GameBridge] Failed to post message:', error);
    }
  }

  /**
   * Queue event if SDK not ready, or send immediately
   */
  private sendOrQueue(message: object): void {
    if (this.isSDKReady) {
      console.log('📤 [GameBridge] Sending immediately:', (message as any).type || 'unknown');
      this.postMessage(message);
    } else {
      console.log('📦 [GameBridge] Queueing (SDK not ready):', (message as any).type || 'unknown', '| Queue size:', this.pendingEvents.length + 1);
      this.pendingEvents.push(message);
    }
  }

  /**
   * Flush pending events after SDK is ready
   */
  private flushPendingEvents(): void {
    const events = [...this.pendingEvents];
    console.log(`🚀 [GameBridge] Flushing ${events.length} pending events`);
    this.pendingEvents = [];
    
    events.forEach(event => {
      this.postMessage(event);
    });
  }

  // ===========================================================================
  // Public API - Subscribe to messages
  // ===========================================================================

  /**
   * Subscribe to messages from game
   * Returns unsubscribe function
   */
  onMessage(handler: GameMessageHandler): () => void {
    this.gameMessageHandlers.add(handler);
    return () => {
      this.gameMessageHandlers.delete(handler);
    };
  }

  /**
   * Subscribe to SDK ready event
   * Returns unsubscribe function
   */
  onSDKReady(handler: SDKReadyHandler): () => void {
    this.sdkReadyHandlers.add(handler);
    
    // If SDK is already ready, call immediately
    if (this.isSDKReady && this.sdkVersion) {
      handler({ version: this.sdkVersion, modules: this.sdkModules });
    }
    
    return () => {
      this.sdkReadyHandlers.delete(handler);
    };
  }

  // ===========================================================================
  // Public API - Send events to game
  // ===========================================================================

  /**
   * Send a stream chat event
   */
  sendStreamChat(comment: StreamingComment): void {
    this.sendOrQueue(formatStreamChat(comment));
  }

  /**
   * Send a stream gift event
   */
  sendStreamGift(gift: StreamingGift): void {
    this.sendOrQueue(formatStreamGift(gift));
  }

  /**
   * Send a stream like event
   */
  sendStreamLike(like: StreamingLike): void {
    this.sendOrQueue(formatStreamLike(like));
  }

  /**
   * Send user data to game
   */
  sendUserData(user: SDKUserData): void {
    this.sendOrQueue(formatUserData(user));
  }

  /**
   * Send user signed out event
   */
  sendUserSignedOut(): void {
    this.sendOrQueue(formatUserSignedOut());
  }

  /**
   * Send app preferences to game
   */
  sendAppPreferences(preferences: AppPreferences): void {
    this.sendOrQueue(formatAppPreferences(preferences));
  }

  /**
   * Send app-bridge response to game
   */
  sendAppBridgeResponse(requestId: number, success: boolean, data?: any, error?: string): void {
    // Responses should be sent immediately
    this.postMessage(formatAppBridgeResponse(requestId, success, data, error));
  }

  /**
   * Send a raw event to game (for custom events)
   */
  sendRawEvent(type: string, data: any): void {
    console.log(`🎮 [GameBridge] sendRawEvent: ${type} | SDK ready: ${this.isSDKReady} | Attached: ${!!this.iframe}`);
    
    // Log full gift data for debugging
    if (type === 'stream-gift') {
      console.log('🎁 [GameBridge] Full gift event data:', JSON.stringify(data, null, 2));
    }
    
    this.sendOrQueue({ type, data });
  }

  /**
   * Send a test message to verify postMessage is working
   */
  sendTestMessage(): void {
    console.log('🧪 [GameBridge] Sending TEST message to iframe...');
    const testMessage = {
      type: 'test-message',
      data: {
        message: 'Hello from WebPlay!',
        timestamp: Date.now()
      }
    };
    this.postMessage(testMessage);
    console.log('🧪 [GameBridge] Test message sent:', testMessage);
  }

  // ===========================================================================
  // Public API - Status
  // ===========================================================================

  /**
   * Check if SDK is ready
   */
  isReady(): boolean {
    return this.isSDKReady;
  }

  /**
   * Get SDK version
   */
  getSDKVersion(): string | null {
    return this.sdkVersion;
  }

  /**
   * Get available SDK modules
   */
  getSDKModules(): string[] {
    return [...this.sdkModules];
  }

  /**
   * Check if a specific module is available
   */
  hasModule(moduleName: string): boolean {
    return this.sdkModules.includes(moduleName);
  }

  /**
   * Check if attached to an iframe
   */
  isAttached(): boolean {
    return this.iframe !== null;
  }
}

// Export singleton instance
export const gameBridgeService = new GameBridgeService();

// Also export the class for testing
export { GameBridgeService };

